import { useState, useEffect, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { inviteUsers } from '../../features/users/api';
import type { CreateInvitationResponse } from '../../features/users/types';
import { formatDate } from '../../utils/formatters';
import styles from './InviteUserModal.module.css';
import '../../styles/buttons.css';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onInvited?: () => void;
};

export function InviteUserModal({ isOpen, onClose, onInvited }: Props) {
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CreateInvitationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // モーダルが開いたときに入力欄をクリア
  useEffect(() => {
    if (isOpen) {
      setEmails('');
      setRole('user');
      setResult(null);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmails = emails.trim();
    if (!trimmedEmails) return;

    setError(null);
    setResult(null);
    setIsLoading(true);

    try {
      const response = await inviteUsers({ emails: trimmedEmails, role });
      setResult(response);

      // すべて成功した場合は自動的に閉じる
      if (response.invited.length > 0 &&
          response.already_exists.length === 0 &&
          response.already_invited.length === 0 &&
          response.belongs_to_other_org.length === 0 &&
          (response.recently_declined?.length ?? 0) === 0 &&
          (response.seat_limit_reached?.length ?? 0) === 0 &&
          response.invalid_emails.length === 0 &&
          response.failed.length === 0) {
        setTimeout(() => {
          onInvited?.();
          onClose();
        }, 1500);
      } else {
        onInvited?.();
      }
    } catch (err: unknown) {
      console.error('Failed to invite users:', err);
      const errorMessage = (
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
      )
        ? (err as { response?: { data?: { error?: string } } }).response!.data!.error!
        : (
          err instanceof Error
            ? err.message
            : '招待に失敗しました'
        );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-card modal-card--md" onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className="modal-header">
          <h2 className="modal-title">ユーザーを招待</h2>
          <button
            type="button"
            onClick={handleClose}
            className="modal-close-button"
            aria-label="閉じる"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        {/* フォーム */}
        <form onSubmit={handleSubmit} className="modal-body">
          <div className={styles.formGroup}>
            <label htmlFor="emails" className={styles.label}>
              メールアドレス
            </label>
            <p className={styles.hint}>
              カンマ区切りまたは改行で複数のメールアドレスを入力できます
            </p>
            <textarea
              id="emails"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              className={styles.textarea}
              placeholder="user1@example.com, user2@example.com&#10;user3@example.com"
              rows={6}
              disabled={isLoading}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="role" className={styles.label}>
              招待時の権限
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
              className={styles.select}
              disabled={isLoading}
            >
              <option value="user">一般ユーザー</option>
              <option value="admin">管理者</option>
            </select>
          </div>

          {/* エラー表示 */}
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {/* 結果表示 */}
          {result && (
            <div className={styles.resultContainer}>
              {result.invited.length > 0 && (
                <div className={styles.successMessage}>
                  ✓ {result.invited.length}件の招待メールを送信しました
                  <ul className={styles.emailList}>
                    {result.invited.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.already_exists.length > 0 && (
                <div className={styles.warningMessage}>
                  以下のメールアドレスは既に登録されています:
                  <ul className={styles.emailList}>
                    {result.already_exists.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.already_invited.length > 0 && (
                <div className={styles.warningMessage}>
                  以下のメールアドレスは既に招待済みです:
                  <ul className={styles.emailList}>
                    {result.already_invited.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.belongs_to_other_org.length > 0 && (
                <div className={styles.warningMessage}>
                  以下のメールアドレスは同一パートナー内の別組織に既に登録されているため招待できません。
                  別のメールアドレスをご利用いただくか、サポートまでご連絡ください:
                  <ul className={styles.emailList}>
                    {result.belongs_to_other_org.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(result.recently_declined?.length ?? 0) > 0 && (
                <div className={styles.warningMessage}>
                  以下のメールアドレスは本人が招待を辞退したため、しばらく再招待できません:
                  <ul className={styles.emailList}>
                    {result.recently_declined?.map(({ email, retry_after }) => (
                      <li key={email}>
                        {email}（{formatDate(retry_after)}まで）
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(result.seat_limit_reached?.length ?? 0) > 0 && (
                <div className={styles.warningMessage}>
                  現在のプランのユーザー数上限に達しているため、以下のメールアドレスは招待できませんでした。
                  プランのアップグレード、または既存ユーザーの削除が必要です:
                  <ul className={styles.emailList}>
                    {result.seat_limit_reached?.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.invalid_emails.length > 0 && (
                <div className={styles.errorMessage}>
                  以下のメールアドレスは無効です:
                  <ul className={styles.emailList}>
                    {result.invalid_emails.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.failed.length > 0 && (
                <div className={styles.errorMessage}>
                  以下のメールアドレスは招待に失敗しました:
                  <ul className={styles.emailList}>
                    {result.failed.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ボタン */}
          <div className="modal-actions">
            <button
              type="submit"
              className="btn-modal btn-modal-accent"
              disabled={isLoading || !emails.trim()}
            >
              {isLoading ? '送信中...' : '招待メールを送信'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
